import { faDice } from '@fortawesome/pro-light-svg-icons';
import { faBookOpen, faBrain, faBriefcase, faBrowser, faBuildingColumns, faBullseyeArrow, faChalkboardUser, faCircleDollar, faCrown, faEllipsisStroke, faFlag, faFlagSwallowtail, faFolderOpen, faGrid2Plus, faMedal, faNetworkWired, faPen, faRectanglesMixed, faTableTree, faTerminal, faTrophy, faUpload, faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ItemProps } from '@tryhackme/thm-ui-components/dist/components/atoms/desktop-nav/desktop-nav.types';
import type { VisibleIn } from '@tryhackme/thm-ui-components/dist/components/compounds/dropdown-menu/dropdown-menu.types';
import { useMemo } from 'react';
import { type ThemeColorMode, UserRoles } from 'src/common/enums';
import { CertificationExamAvailabilityStatus, CertificationType } from 'src/common/enums/certification';
import { useFeatureIsOn } from 'src/common/growthbook';
import { B2BUserHelper, extractB2BUserRoles } from 'src/common/helpers';
import { useUserCompanyFeature } from 'src/common/hooks';
import { useGetUserQuery } from 'src/common/slices';
import { CompanyFeatureName } from 'src/common/utils/company-features';
import { useGetCertificationExamsDataQuery } from 'src/features/certifications/certification.slice';
import { useGetWorkspaceQuery } from '../management-dashboard/workspace/workspace.slice';
import { CustomNavbarIcon } from './components';
import { CustomNavbarIconTypes } from './components/custom-navbar-icon/custom-navbar-icon.types';
import { CERTIFICATION_CONFIGS } from './constants/certification-config';
import { StyledTableTopIcon } from './header.styles';
export const conditionalLink = <T,>(condition: boolean, link: T) => condition ? [link] : [];
const useCertificationLinks = (userId?: string) => {
  const unavailableTypesQuery = useGetCertificationExamsDataQuery({
    filters: {
      status: CertificationExamAvailabilityStatus.UNAVAILABLE
    }
  }, {
    skip: !userId
  });
  return CERTIFICATION_CONFIGS.map(config => {
    const unavailableCert = unavailableTypesQuery.data?.find(cert => cert.certificationType === config.type);
    const link = unavailableCert ? config.basePath : `${config.basePath}/details`;
    return {
      ...config,
      href: link
    };
  });
};
export const useMainNavigationLinks = (forcedTheme?: ThemeColorMode): ItemProps[] => {
  const {
    data: getUserQueryData
  } = useGetUserQuery();
  const isTTXEnabled = useUserCompanyFeature(CompanyFeatureName.TTX, getUserQueryData?.data?.user);
  const isWeeklyLeaguesEnabled = useFeatureIsOn('weekly-leagues-page');
  const isCertificationNavEnabled = useFeatureIsOn('certification-on-learn-main-nav');
  const isPT1ExamEnabled = useFeatureIsOn('pt1-exam');
  const isNavTTXEnabled = useFeatureIsOn('nav-ttx') && isTTXEnabled;
  const isNewNavEnabled = useFeatureIsOn('new-navigation');
  const isMgtDashboardV3Enabled = useFeatureIsOn('mgt-dashboard-v3');
  const isBusinessLandingPageEnabled = useFeatureIsOn('business-landing-page');
  const {
    data: WorkspaceData
  } = useGetWorkspaceQuery(undefined, {
    skip: !getUserQueryData?.data?.user
  });
  const certificationLinks = useCertificationLinks(getUserQueryData?.data?.user?._id);
  const isCtfBuilderEnabled = useUserCompanyFeature(CompanyFeatureName.CTF_BUILDER, getUserQueryData?.data?.user);
  return useMemo(() => {
    const userData = getUserQueryData?.data?.user;
    const isAdmin = userData?.accountType === 'admin';
    const userB2BUserRoles = extractB2BUserRoles(userData);
    const isRoomDeveloper = userData?.roles?.includes(UserRoles.ROOM_MAKER);
    const isContentDevInternal = (isAdmin || userData?.roles?.includes(UserRoles.CONTENT_DEV_INTERNAL)) ?? false;
    const {
      isBusinessUser
    } = B2BUserHelper(userData);
    const isWorkspaceUser = !!WorkspaceData?.hasJoinedWorkspace;
    const isB2BOrWorkspaceUser = isBusinessUser || isWorkspaceUser;
    const LearnItems = {
      name: 'Learn',
      icon: <FontAwesomeIcon icon={faBookOpen} />,
      visibleIn: 'both' as VisibleIn,
      ...(isNewNavEnabled && {
        href: '/hacktivities'
      }),
      subItems: [...(isNewNavEnabled ? [] : [{
        name: 'Learn',
        icon: <FontAwesomeIcon icon={faBookOpen} />,
        description: 'Hands-on training',
        href: '/hacktivities',
        visibleIn: 'both' as VisibleIn
      }, {
        name: 'Challenges',
        icon: <FontAwesomeIcon icon={faBullseyeArrow} />,
        description: 'Reinforce your learning',
        href: `/hacktivities/challenges`,
        visibleIn: 'both' as VisibleIn,
        isNew: false
      }, ...conditionalLink(isCertificationNavEnabled, {
        name: 'Get certified',
        icon: <CustomNavbarIcon type={CustomNavbarIconTypes.CERTIFICATION} forcedTheme={forcedTheme} />,
        description: 'Verify your skills',
        visibleIn: 'both' as VisibleIn,
        isNew: false,
        subMenuItems: certificationLinks.filter(cert => cert.type !== CertificationType.PT1 || isPT1ExamEnabled).map(cert => ({
          name: cert.name,
          icon: <CustomNavbarIcon type={CustomNavbarIconTypes.CERTIFICATION} forcedTheme={forcedTheme} />,
          description: cert.description,
          href: cert.href,
          visibleIn: 'both' as VisibleIn,
          isNew: false
        }))
      }), {
        name: 'SOC Simulator',
        icon: <CustomNavbarIcon type={CustomNavbarIconTypes.SOC_SIM} forcedTheme={forcedTheme} />,
        description: 'Triage alerts in realtime',
        href: '/soc-sim/?ref=nav',
        visibleIn: 'both' as VisibleIn,
        isNew: false
      }, {
        name: 'Threat Hunting Simulator',
        icon: <CustomNavbarIcon type={CustomNavbarIconTypes.TH_SIM} forcedTheme={forcedTheme} />,
        description: 'Reconstruct the attack chain',
        href: '/threat-hunting-sim/?ref=nav',
        visibleIn: 'both' as VisibleIn,
        isNew: true
      }, ...conditionalLink(isNavTTXEnabled, {
        name: 'Tabletop Exercises',
        // TODO: Replace StyledTableTopIcon with FontAwesomeIcon once isNew tag is removed
        icon: <StyledTableTopIcon icon={faDice} />,
        description: 'Test response readiness',
        href: isB2BOrWorkspaceUser ? '/tabletop-exercises/exercises' : '/tabletop-exercises',
        visibleIn: 'both' as VisibleIn,
        isNew: true
      } as const)])]
    } satisfies ItemProps;
    return [{
      name: 'Dashboard',
      icon: <FontAwesomeIcon icon={faBrowser} />,
      href: '/dashboard',
      visibleIn: 'private' as VisibleIn,
      visibleInSplitScreen: true
    }, {
      ...LearnItems,
      visibleIn: 'both' as VisibleIn,
      visibleInSplitScreen: true
    }, ...conditionalLink(Boolean(isNewNavEnabled), {
      name: 'Practice',
      icon: <FontAwesomeIcon icon={faBullseyeArrow} />,
      visibleIn: 'both',
      visibleInSplitScreen: true,
      subItems: [{
        name: 'Challenges',
        icon: <FontAwesomeIcon icon={faFlag} />,
        description: 'Reinforce your learning',
        href: `/challenges`,
        visibleIn: 'both' as VisibleIn,
        isNew: false
      }, {
        name: 'SOC Simulator',
        icon: <CustomNavbarIcon type={CustomNavbarIconTypes.SOC_SIM} forcedTheme={forcedTheme} />,
        description: 'Triage alerts in realtime',
        href: '/soc-sim/?ref=nav',
        visibleIn: 'both' as VisibleIn,
        isNew: false
      }, {
        name: 'Threat Hunting Simulator',
        icon: <CustomNavbarIcon type={CustomNavbarIconTypes.TH_SIM} forcedTheme={forcedTheme} />,
        description: 'Reconstruct the attack chain',
        href: '/threat-hunting-sim/?ref=nav',
        visibleIn: 'both' as VisibleIn,
        isNew: true
      }, ...conditionalLink(isCertificationNavEnabled, {
        name: 'Get certified',
        icon: <CustomNavbarIcon type={CustomNavbarIconTypes.CERTIFICATION} forcedTheme={forcedTheme} />,
        description: 'Verify your skills',
        visibleIn: 'both' as VisibleIn,
        isNew: false,
        subMenuItems: certificationLinks.filter(cert => cert.type !== CertificationType.PT1 || isPT1ExamEnabled).map(cert => ({
          name: cert.name,
          icon: <CustomNavbarIcon type={CustomNavbarIconTypes.CERTIFICATION} forcedTheme={forcedTheme} />,
          description: cert.description,
          href: cert.href,
          visibleIn: 'both' as VisibleIn,
          isNew: false
        }))
      }), ...conditionalLink(isNavTTXEnabled, {
        name: 'Tabletop Exercises',
        // TODO: Replace StyledTableTopIcon with FontAwesomeIcon once isNew tag is removed
        icon: <StyledTableTopIcon icon={faDice} />,
        description: 'Test response readiness',
        href: isB2BOrWorkspaceUser ? '/tabletop-exercises/exercises' : '/tabletop-exercises',
        visibleIn: 'both' as VisibleIn,
        isNew: true
      } as const)]
    } as ItemProps), {
      name: 'Compete',
      icon: <FontAwesomeIcon icon={faTrophy} />,
      visibleIn: 'public' as VisibleIn,
      visibleInSplitScreen: true,
      subItems: [...conditionalLink(isWeeklyLeaguesEnabled, {
        name: 'Weekly Leagues',
        icon: <FontAwesomeIcon icon={faMedal} />,
        description: 'Earn points & Win',
        href: '/leagues',
        visibleIn: 'private' as VisibleIn
      } as const), {
        name: 'King of the Hill',
        icon: <FontAwesomeIcon icon={faCrown} />,
        description: 'Attack & Defend',
        href: '/games/koth',
        visibleIn: 'public' as VisibleIn
      }, {
        name: 'Leaderboards',
        icon: <FontAwesomeIcon icon={faTrophy} />,
        description: 'Platform Rankings',
        href: '/leaderboards',
        visibleIn: 'public' as VisibleIn
      }]
    }, {
      name: 'Compete',
      icon: <FontAwesomeIcon icon={faTrophy} />,
      visibleIn: 'private' as VisibleIn,
      subItems: [...conditionalLink(isWeeklyLeaguesEnabled, {
        name: 'Weekly Leagues',
        icon: <FontAwesomeIcon icon={faMedal} />,
        description: 'Earn points & Win',
        href: '/leagues',
        visibleIn: 'private' as VisibleIn
      } as const), {
        name: 'Leaderboards',
        icon: <FontAwesomeIcon icon={faTrophy} />,
        description: 'Platform Rankings',
        href: '/leaderboards',
        visibleIn: 'private' as VisibleIn
      }, {
        name: 'King of the Hill',
        icon: <FontAwesomeIcon icon={faCrown} />,
        description: 'Attack & Defend',
        href: '/games/koth',
        visibleIn: 'private' as VisibleIn
      }, ...conditionalLink(!isNewNavEnabled, {
        name: 'Workspace',
        icon: <FontAwesomeIcon icon={faUserGroup} />,
        description: 'Compete & Collaborate',
        href: '/workspace',
        visibleIn: 'private' as VisibleIn
      }), ...conditionalLink(isB2BOrWorkspaceUser && !isNewNavEnabled, {
        name: 'CTF Builder',
        icon: <FontAwesomeIcon icon={faFlagSwallowtail} />,
        href: '/ctf-builder',
        description: 'Create your own CTF event',
        visibleIn: 'private' as VisibleIn
      } as const)]
    }, ...conditionalLink(Boolean(isRoomDeveloper), {
      name: 'Develop',
      icon: <FontAwesomeIcon icon={faTerminal} />,
      subItems: [{
        name: 'Upload',
        icon: <FontAwesomeIcon icon={faUpload} />,
        description: 'Upload VMs & Files',
        href: '/upload',
        visibleIn: 'private' as VisibleIn
      }, {
        name: 'Manage Rooms',
        icon: <FontAwesomeIcon icon={faTableTree} />,
        description: 'Create and Manage Rooms',
        href: '/rooms?tab=manage-rooms',
        visibleIn: 'private' as VisibleIn
      }, ...conditionalLink(isContentDevInternal, {
        name: 'Manage Networks',
        icon: <FontAwesomeIcon icon={faNetworkWired} />,
        description: 'Create and Manage Networks',
        href: '/networks',
        visibleIn: 'private' as VisibleIn
      } as const), ...conditionalLink(isContentDevInternal, {
        name: 'AI Agents',
        icon: <FontAwesomeIcon icon={faBrain} />,
        description: 'Create and Manage AI Agents',
        href: '/develop/ai-agents',
        visibleIn: 'private' as VisibleIn
      } as const), {
        name: 'Your Material',
        icon: <FontAwesomeIcon icon={faFolderOpen} />,
        description: 'View your uploads',
        href: '/your-material',
        visibleIn: 'private' as VisibleIn
      }],
      visibleIn: 'private' as VisibleIn
    } as ItemProps), ...conditionalLink(isNewNavEnabled && isBusinessUser, {
      name: 'Business',
      icon: <FontAwesomeIcon icon={faBriefcase} />,
      visibleIn: 'private' as VisibleIn,
      visibleInSplitScreen: true,
      subItems: [...conditionalLink(userB2BUserRoles.isCompanyAdmin, {
        name: 'Management Dashboard',
        icon: <FontAwesomeIcon icon={faRectanglesMixed} />,
        description: 'Licenses, assignments & reporting',
        href: `/client/${userData?.companies?.[0]?._id ?? ''}${isMgtDashboardV3Enabled ? '/dashboard' : ''}`,
        visibleIn: 'private' as VisibleIn
      }), {
        name: 'Workspace',
        icon: <FontAwesomeIcon icon={faUserGroup} />,
        description: 'Compete & Collaborate',
        href: '/workspace',
        visibleIn: 'private' as VisibleIn
      }, ...conditionalLink(isCtfBuilderEnabled, {
        name: 'CTF Builder',
        icon: <FontAwesomeIcon icon={faFlagSwallowtail} />,
        href: '/ctf-builder',
        description: 'Create your own CTF event',
        visibleIn: 'private' as VisibleIn
      } as const), ...conditionalLink(userB2BUserRoles.isB2BUserActive, {
        name: 'Assignments',
        icon: <FontAwesomeIcon icon={faPen} />,
        description: 'Learn, practice, progress',
        href: '/assignments',
        visibleIn: 'private' as VisibleIn
      } as const)]
    }), {
      name: 'Education',
      icon: <FontAwesomeIcon icon={faBuildingColumns} />,
      subItems: [{
        name: 'Teaching',
        icon: <FontAwesomeIcon icon={faChalkboardUser} />,
        description: 'Use our security labs',
        href: '/classrooms',
        visibleIn: 'public' as VisibleIn
      }, {
        name: 'Create labs',
        icon: <FontAwesomeIcon icon={faGrid2Plus} />,
        description: 'Upload & deploy VMs',
        href: '/develop-rooms',
        visibleIn: 'public' as VisibleIn
      }],
      visibleIn: 'public' as VisibleIn
    }, {
      name: 'Business',
      icon: <FontAwesomeIcon icon={faBriefcase} />,
      href: isBusinessLandingPageEnabled ? '/business' : 'https://business.tryhackme.com',
      visibleIn: 'public' as VisibleIn,
      visibleInSplitScreen: true
    }, {
      name: 'Pricing',
      icon: <FontAwesomeIcon icon={faCircleDollar} />,
      href: '/pricing',
      visibleIn: 'public' as VisibleIn
    }, ...conditionalLink(!isNewNavEnabled, {
      name: 'Other',
      icon: <FontAwesomeIcon icon={faEllipsisStroke} />,
      subItems: [...conditionalLink(userB2BUserRoles.isCompanyAdmin && !isNewNavEnabled, {
        name: 'Management Dashboard',
        href: `/client/${userData?.companies?.[0]?._id ?? ''}${isMgtDashboardV3Enabled ? '/dashboard' : ''}`
      }), ...conditionalLink(!isNewNavEnabled, {
        name: 'For Business',
        href: 'https://business.tryhackme.com'
      }), ...conditionalLink(!isNewNavEnabled, {
        name: 'For Education',
        href: '/classrooms'
      }), ...conditionalLink(!isNewNavEnabled, {
        name: 'Career Hub',
        href: '/careers'
      }), ...conditionalLink(!isNewNavEnabled, {
        name: 'Resources',
        href: '/resources/blog'
      }), ...conditionalLink(!isNewNavEnabled, {
        name: 'Glossary',
        href: '/glossary'
      }), ...conditionalLink(!isNewNavEnabled, {
        name: 'Buy Vouchers',
        href: '/subscriptions'
      }), ...conditionalLink(!isNewNavEnabled, {
        name: 'Develop Rooms',
        href: '/develop-rooms'
      }), ...conditionalLink(!isNewNavEnabled, {
        name: 'Swag Shop',
        href: '/swag-shop'
      })],
      visibleIn: 'private' as VisibleIn,
      visibleInSplitScreen: true
    })];
  }, [getUserQueryData?.data?.user, WorkspaceData?.hasJoinedWorkspace, isNavTTXEnabled, isNewNavEnabled, isCertificationNavEnabled, certificationLinks, isWeeklyLeaguesEnabled, isPT1ExamEnabled, forcedTheme, isMgtDashboardV3Enabled, isBusinessLandingPageEnabled, isCtfBuilderEnabled]);
};